import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/**
 * SAT · Rechtliche Seiten
 * Aktualisiert 25.02.2026 gemäss Word-Auftrag „SAT-Homepage".
 *
 * Firmensitz: SAT-Akademie, Pilatusstrasse 9, 6312 Steinhausen, Schweiz
 * Geschäftsführer: Nenad Vladović
 * Kontakt: info@sat-akademie.com
 *
 * AGB-Stand: Zug, 12.11.2022 (unverändert übernommen).
 * Datenschutzerklärung: mit SAT-Charakter (Respekt · Transparenz · achtsamer Umgang).
 *
 * Hinweis: Diese Seiten sind mit den bereitgestellten Volltexten hinterlegt.
 * Die Platzhalter-Banner sind entfernt, da SAT den Inhalt selbst freigegeben hat.
 */

const LegalLayout = ({ title, kicker, children, noindex = false, path }) => (
  <div className="pt-24 pb-16 min-h-screen">
    <SEO title={title} path={path} noindex={noindex} />
    <div className="container-sat max-w-3xl">
      <div className="overline">{kicker}</div>
      <h1 className="serif font-light text-4xl md:text-5xl mt-2 mb-8" style={{ color: "#D4AF37" }}>{title}</h1>
      <div className="prose prose-invert max-w-none text-white/85 leading-relaxed space-y-4">{children}</div>
      <div className="mt-12 pt-6 border-t border-white/10 text-xs text-white/50">
        <Link to="/" className="hover:text-white" data-testid="legal-back-home">← Zurück zur Startseite</Link>
      </div>
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="serif text-2xl mt-10 mb-3 text-white">{children}</h2>
);

// ---------------------------------------------------------------------------
// IMPRESSUM
// ---------------------------------------------------------------------------
export const Impressum = () => (
  <LegalLayout kicker="SAT · Rechtliches" title="Impressum" path="/impressum">
    <p><strong className="text-white">SAT-Akademie · Schweizer Akademie für Transformation</strong></p>
    <p>
      Pilatusstrasse 9<br />
      6312 Steinhausen<br />
      Schweiz
    </p>
    <p>
      E-Mail: <a href="mailto:info@sat-akademie.com" style={{ color: "#D4AF37" }} data-testid="impressum-email">info@sat-akademie.com</a><br />
      Web: <a href="https://sat-akademie.com" style={{ color: "#D4AF37" }}>sat-akademie.com</a>
    </p>
    <p><strong className="text-white">Geschäftsführer:</strong> Nenad Vladović</p>

    <H2>Haftungsausschluss</H2>
    <p>
      Der Autor übernimmt keinerlei Gewähr hinsichtlich der inhaltlichen Richtigkeit, Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen. Haftungsansprüche gegen den Autor wegen Schäden materieller oder immaterieller Art, welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten Informationen, durch Missbrauch der Verbindung oder durch technische Störungen entstanden sind, werden ausgeschlossen.
    </p>
    <p>
      Alle Angebote sind unverbindlich. Der Autor behält es sich ausdrücklich vor, Teile der Seiten oder das gesamte Angebot ohne gesonderte Ankündigung zu verändern, zu ergänzen, zu löschen oder die Veröffentlichung zeitweise oder endgültig einzustellen.
    </p>

    <H2>Haftung für Links</H2>
    <p>
      Verweise und Links auf Webseiten Dritter liegen ausserhalb unseres Verantwortungsbereichs. Es wird jegliche Verantwortung für solche Webseiten abgelehnt. Der Zugriff und die Nutzung solcher Webseiten erfolgt auf eigene Gefahr des Nutzers.
    </p>

    <H2>Urheberrechte</H2>
    <p>
      Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien auf dieser Website gehören ausschliesslich der SAT-Akademie · Nenad Vladović · oder den speziell genannten Rechteinhabern. Für die Reproduktion jeglicher Elemente ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.
    </p>
  </LegalLayout>
);

// ---------------------------------------------------------------------------
// DATENSCHUTZ · mit SAT-Charakter
// ---------------------------------------------------------------------------
export const Datenschutz = () => (
  <LegalLayout kicker="SAT · Rechtliches" title="Datenschutzerklärung" path="/datenschutz">
    <H2>1 · Einleitung</H2>
    <p>
      Die SAT-Akademie steht für <em>Respekt, Transparenz und einen achtsamen Umgang</em> — auch mit deinen Daten. Diese Erklärung informiert dich verständlich darüber, welche personenbezogenen Daten wir erheben, wie wir sie verwenden und welche Rechte du in diesem Zusammenhang hast.
    </p>
    <p>
      Wir behandeln deine Angaben so, wie wir selbst behandelt werden möchten: sorgfältig, zweckgebunden und nur so lange wie nötig.
    </p>

    <H2>2 · Verantwortliche Stelle</H2>
    <p>
      SAT-Akademie · Nenad Vladović<br />
      Pilatusstrasse 9 · 6312 Steinhausen · Schweiz<br />
      <a href="mailto:info@sat-akademie.com" style={{ color: "#D4AF37" }}>info@sat-akademie.com</a>
    </p>

    <H2>3 · Welche Daten wir erfassen</H2>
    <p><strong className="text-white">Logfiles.</strong> Bei jedem Besuch werden technische Informationen (IP-Adresse, Browsertyp, Datum, aufgerufene Seite) automatisch protokolliert. Diese Angaben dienen ausschliesslich der Sicherstellung der Stabilität, Sicherheit und Funktionalität unseres Angebots.</p>
    <p><strong className="text-white">Kontaktformulare & Erstgespräch.</strong> Wenn du uns über ein Formular kontaktierst, erhalten wir die von dir freiwillig eingegebenen Daten (Name, E-Mail, Anliegen). Wir nutzen sie ausschliesslich zur Bearbeitung deiner Anfrage.</p>
    <p><strong className="text-white">Kursbuchungen & Zahlungen.</strong> Für Kurse, Retreats und Programme werden die zur Vertragsabwicklung notwendigen Daten erhoben. Zahlungen werden über den Zahlungsdienstleister Stripe abgewickelt — die Zahlungsdaten selbst erreichen SAT nicht.</p>
    <p><strong className="text-white">Cookies.</strong> Wir setzen ausschliesslich technisch notwendige Cookies für Session-Verwaltung und Sicherheitsfunktionen. Cookies lassen sich in deinem Browser jederzeit blockieren; einige Funktionen unserer Website stehen dann möglicherweise nur eingeschränkt zur Verfügung.</p>

    <H2>4 · Zweck der Datennutzung</H2>
    <p>
      Wir verwenden deine Daten ausschliesslich zur Bereitstellung, Optimierung und Absicherung unserer Dienstleistungen sowie zur Kommunikation mit dir. Eine Nutzung für andere Zwecke — insbesondere Werbung ohne deine Zustimmung — findet nicht statt.
    </p>

    <H2>5 · Newsletter</H2>
    <p>
      Deine E-Mail-Adresse verwenden wir für den SAT-Newsletter nur nach ausdrücklicher Einwilligung. Er enthält Neuigkeiten, Veranstaltungshinweise und ausgewählte Angebote. Eine Abmeldung ist jederzeit über den Link am Ende jeder E-Mail oder per Nachricht an <a href="mailto:info@sat-akademie.com" style={{ color: "#D4AF37" }}>info@sat-akademie.com</a> möglich.
    </p>

    <H2>6 · Externe Dienste</H2>
    <p>
      Für Hosting, Zahlungen, Newsletter und Terminvereinbarung arbeiten wir mit sorgfältig ausgewählten Auftragsverarbeitern zusammen (u. a. Stripe, Resend, Calendly, MongoDB Atlas). Diese verarbeiten deine Daten nur im Auftrag der SAT-Akademie und ausschliesslich gemäss unseren Vorgaben — unter Wahrung anerkannter Sicherheitsstandards.
    </p>

    <H2>7 · Deine Rechte</H2>
    <p>Dir stehen jederzeit die folgenden Rechte zu:</p>
    <ul className="list-disc list-inside space-y-1">
      <li>Auskunft über die zu deiner Person gespeicherten Daten</li>
      <li>Berichtigung unrichtiger oder unvollständiger Angaben</li>
      <li>Löschung deiner Daten, sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen</li>
      <li>Einschränkung der Verarbeitung</li>
      <li>Widerruf erteilter Einwilligungen — mit Wirkung für die Zukunft</li>
    </ul>
    <p>
      Deine Rechte machst du unkompliziert per E-Mail an <a href="mailto:info@sat-akademie.com" style={{ color: "#D4AF37" }}>info@sat-akademie.com</a> geltend. Zusätzlich hast du das Recht, dich bei der zuständigen Datenschutzbehörde zu beschweren — in der Schweiz beim Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (<a href="https://www.edoeb.admin.ch" target="_blank" rel="noopener noreferrer" style={{ color: "#D4AF37" }}>edoeb.admin.ch</a>).
    </p>

    <H2>8 · Änderungen dieser Datenschutzerklärung</H2>
    <p>
      Wir behalten uns vor, diese Erklärung anzupassen, um rechtlichen Anforderungen zu entsprechen oder Änderungen unserer Dienstleistungen abzubilden. Die jeweils aktuelle Fassung ist stets auf dieser Seite verfügbar.
    </p>

    <p className="text-xs text-white/50 mt-8"><em>Zug, Stand 25. Februar 2026</em></p>
  </LegalLayout>
);

// ---------------------------------------------------------------------------
// AGB · Volltext gemäss Freigabe vom 12.11.2022
// ---------------------------------------------------------------------------
export const AGB = () => (
  <LegalLayout kicker="SAT · Rechtliches" title="Allgemeine Geschäftsbedingungen" path="/agb">
    <H2>1 · Geltungsbereich</H2>
    <p>
      Diese Allgemeinen Geschäftsbedingungen regeln das Vertragsverhältnis zwischen der SAT-Akademie (nachfolgend &bdquo;SAT&ldquo; genannt) und ihren Klientinnen und Klienten. Sie gelten für Dienstverträge gemäss dem Schweizer Obligationenrecht.
    </p>

    <H2>2 · Gegenstand des Vertrages</H2>
    <p>
      Gegenstand des Vertrages ist die Erbringung der vereinbarten Leistungen durch SAT gegenüber der Klientin oder dem Klienten. Eine ausdrückliche Erfolgsgarantie wird ausdrücklich nicht abgegeben; die Ergebnisse hängen von der Mitwirkung und der individuellen Situation der Klient:innen ab.
    </p>

    <H2>3 · Dienstleistungen</H2>
    <p>Die SAT-Akademie bietet unter anderem folgende Leistungen an:</p>
    <ul className="list-disc list-inside space-y-1">
      <li>Coaching, Beratung und Schulungen in beruflichen und privaten Belangen</li>
      <li>Weiterbildungen im Bereich fachlicher und persönlicher Führungskompetenzen</li>
      <li>Formate zu Entspannung, Selbsterfahrung und kognitiver Umstrukturierung</li>
    </ul>

    <H2>4 · Angebote und Honorare</H2>
    <p>
      Angebote der SAT-Akademie sind unverbindlich und können jederzeit angepasst werden. Honorare werden gemäss individueller Vereinbarung festgelegt. Allfällige Zusatzkosten (Reise, Material, externe Räume etc.) werden separat ausgewiesen und in Rechnung gestellt.
    </p>

    <H2>5 · Terminabsagen</H2>
    <p>
      Vereinbarte Termine sind verbindlich. Absagen oder Verschiebungen sind bis <strong>zwei Werktage</strong> im Voraus kostenfrei möglich. Danach werden <strong>50 %</strong> des vereinbarten Honorars in Rechnung gestellt. Bei Nichterscheinen ohne vorherige Absage wird das <strong>volle Honorar</strong> fällig.
    </p>

    <H2>6 · Höhere Gewalt und Leistungshindernisse</H2>
    <p>
      Bei Krankheit, Unfall oder anderen unvorhersehbaren Ereignissen auf Seiten von SAT werden nach Möglichkeit Ersatztermine angeboten. Ein Anspruch auf Kostenerstattung besteht in solchen Fällen nicht.
    </p>

    <H2>7 · Urheberrecht und Vertraulichkeit</H2>
    <p>
      Sämtliche ausgehändigten Unterlagen (Skripte, Workbooks, Videos, Audios etc.) sind ausschliesslich zum persönlichen Gebrauch der Klientinnen und Klienten bestimmt. Eine Weitergabe an Dritte ist ohne schriftliche Zustimmung untersagt. SAT wahrt die Vertraulichkeit über sämtliche im Rahmen der Zusammenarbeit besprochenen Inhalte.
    </p>

    <H2>8 · Haftung</H2>
    <p>
      Die Haftung von SAT ist im gesetzlich zulässigen Rahmen ausgeschlossen. Die Klientin bzw. der Klient trägt die volle Verantwortung für die eigene körperliche, psychische und emotionale Gesundheit sowie für alle im Rahmen der Zusammenarbeit gefassten Entscheidungen und Umsetzungen.
    </p>

    <H2>9 · Mitwirkungspflicht des Klienten</H2>
    <p>
      Die Wirksamkeit einer Zusammenarbeit setzt Kooperation und Vertrauen voraus. Die Klientin bzw. der Klient ist selbst verantwortlich für die aktive Mitgestaltung der eigenen Veränderungsarbeit und die Umsetzung im Alltag.
    </p>

    <H2>10 · Widerrufsrecht</H2>
    <p>
      Es besteht ein Widerrufsrecht von <strong>14 Tagen</strong> ab Vertragsabschluss, sofern die vereinbarte Dienstleistung noch nicht erbracht wurde. Wurde eine digitale Leistung bereits genutzt oder ein Termin bereits stattgefunden, erlischt das Widerrufsrecht.
    </p>

    <H2>11 · Datenschutz</H2>
    <p>
      Persönliche Daten werden vertraulich und im Einklang mit der DSGVO sowie dem Schweizer Datenschutzgesetz behandelt. Sie werden ausschliesslich zur Vertragsabwicklung verwendet. Detaillierte Angaben findest du in unserer <Link to="/datenschutz" style={{ color: "#D4AF37" }}>Datenschutzerklärung</Link>.
    </p>

    <H2>12 · Geistiges Eigentum</H2>
    <p>
      Sämtliche von SAT bereitgestellten Materialien, Konzepte und Methoden sind urheberrechtlich geschützt. Eine Weiterverwendung, Vervielfältigung oder Verbreitung ist nur mit ausdrücklicher schriftlicher Zustimmung von SAT zulässig.
    </p>

    <H2>13 · Anpassungen der AGB</H2>
    <p>
      SAT behält sich vor, diese Allgemeinen Geschäftsbedingungen jederzeit anzupassen. Die jeweils gültige Fassung ist stets auf der Website einsehbar.
    </p>

    <H2>14 · Gerichtsstand und anwendbares Recht</H2>
    <p>
      Gerichtsstand ist Zug, Schweiz. Es findet ausschliesslich Schweizer Recht Anwendung.
    </p>

    <p className="text-xs text-white/50 mt-10"><em>Zug, 12. November 2022</em></p>
  </LegalLayout>
);

// ---------------------------------------------------------------------------
// ÜBER NENAD · Volltext gemäss Freigabe vom User
// ---------------------------------------------------------------------------
export const AboutSAT = () => (
  <div className="pt-24 pb-20 min-h-screen relative overflow-hidden" data-testid="page-about">
    <SEO
      title="Über Nenad Vladović · Gründer der SAT-Akademie"
      description="Mentor für Transformation, Experte für holistische Unternehmensentwicklung — Nenad Vladović über seinen Weg, seine Werte und die Gründung der SAT-Akademie."
      path="/about"
    />

    {/* Subtile dekorative Gold-Aura */}
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `
          radial-gradient(ellipse 40% 30% at 20% 10%, rgba(212,175,55,0.10) 0%, transparent 60%),
          radial-gradient(ellipse 50% 40% at 80% 90%, rgba(212,175,55,0.06) 0%, transparent 65%)
        `,
      }}
    />

    <div className="container-sat max-w-4xl relative">
      {/* Kicker */}
      <div className="overline">Über den Gründer</div>

      {/* Hauptüberschrift */}
      <h1
        className="serif font-light mt-4 mb-4 leading-[1.02]"
        style={{ fontSize: "clamp(2.6rem, 6vw, 4.8rem)" }}
      >
        Nenad <span style={{ color: "#D4AF37" }}>Vladović</span>
      </h1>

      <p
        className="serif italic text-2xl md:text-3xl text-white/85 max-w-3xl leading-snug mb-4"
        data-testid="about-tagline"
      >
        Mentor für Transformation.<br />
        Gründer der SAT.
      </p>

      <p className="text-base md:text-lg text-white/70 max-w-3xl leading-relaxed">
        Als Gründer der SAT, Mentor für Transformation und Experte für holistische Unternehmensentwicklung diene ich meinen Klienten als <span className="text-white">Lebensberater, Unterstützer, Wegweiser und Wegbegleiter</span> in ein erfülltes und glückliches Leben.
      </p>

      {/* Zitat als visueller Anker */}
      <div className="my-14 md:my-20 border-l-2 pl-6 md:pl-8 max-w-2xl" style={{ borderColor: "#D4AF37" }}>
        <p className="serif italic text-xl md:text-2xl text-white/85 leading-snug">
          &bdquo;Ich spürte, dass es noch mehr im Leben geben muss.&ldquo;
        </p>
      </div>

      {/* Herzenswunsch */}
      <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-12 mb-14">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase mb-2" style={{ color: "#D4AF37" }}>Kapitel I</div>
          <div className="serif text-2xl text-white leading-tight">Mein Herzens&shy;wunsch</div>
        </div>
        <div className="text-white/80 leading-relaxed text-base md:text-lg space-y-4">
          <p>
            Bevor ich mir meinen Herzenswunsch erfüllte und Mentor für Transformation wurde, führte mich mein Weg über ein Jura-Studium hin zu einer Tätigkeit im Bankwesen und in Versicherungsgesellschaften.
          </p>
          <p>
            Trotz der vielen Jahre des Erfolgs in der Finanz- und Versicherungsbranche spürte ich, dass es noch mehr im Leben geben muss.
          </p>
          <p>
            Ich begann, mich intensiv mit den Themen der <span className="text-white">Transformation, Mindset, Persönlichkeitsentwicklung sowie Bewusstseinserweiterung</span> auseinanderzusetzen und absolvierte Ausbildungen in Neurosensitivitäts-Coaching, Transformations-Therapie, Human Design System Analytik, Holistische Unternehmensentwicklung und Business Coaching.
          </p>
          <p>
            Diese Reise entwickelte sich zu einer tiefgreifenden Transformation und einem neuen Verständnis des Lebens.
          </p>
        </div>
      </div>

      {/* Erlebnisse */}
      <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-12 mb-14">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase mb-2" style={{ color: "#D4AF37" }}>Kapitel II</div>
          <div className="serif text-2xl text-white leading-tight">Meine Erlebnisse</div>
        </div>
        <div className="text-white/80 leading-relaxed text-base md:text-lg space-y-4">
          <p>
            Ergriffen von meinen Erlebnissen und dem persönlichen Wunsch, anderen Menschen zu mehr <span className="text-white">Bewusstsein und Herzöffnung</span> zu verhelfen, ihr volles Potenzial zu entdecken und sich zu verwirklichen, gründete ich schliesslich im Jahr 2022 die SAT.
          </p>
          <p>
            Eine Akademie für Transformation und Bewusstseinserweiterung — im Dienst der Menschen. Hier kombiniere ich mein Wissen und meine Expertise aus der Businesswelt mit denen aus der Persönlichkeitsentwicklung.
          </p>
        </div>
      </div>

      {/* Gründung */}
      <div className="grid md:grid-cols-[220px_1fr] gap-6 md:gap-12 mb-16">
        <div>
          <div className="text-xs tracking-[0.35em] uppercase mb-2" style={{ color: "#D4AF37" }}>Kapitel III</div>
          <div className="serif text-2xl text-white leading-tight">Die Gründung</div>
        </div>
        <div className="text-white/80 leading-relaxed text-base md:text-lg space-y-6">
          <p>
            Die von mir 2022 gegründete <span className="text-white">SAT — Schweizer Akademie für Transformation</span> beruht auf sechs Werten. Sie tragen alles, was wir tun.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {["Liebe", "Dankbarkeit", "Verbundenheit", "Einheit", "Natürlichkeit", "Individualität"].map((v) => (
              <div
                key={v}
                className="glass rounded-xl px-4 py-3 border border-[#D4AF37]/30 text-center"
                data-testid={`about-value-${v.toLowerCase()}`}
              >
                <span className="serif text-lg" style={{ color: "#D4AF37" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Die drei Hauptwege */}
      <div className="mt-20">
        <div className="overline mb-4">Wenn du Nenad direkt begegnen möchtest</div>
        <div className="grid md:grid-cols-3 gap-4">
          <Link to="/sat-master" className="glass rounded-2xl p-6 hover:border-[#D4AF37]/60 border border-white/10 transition group" data-testid="about-link-master">
            <div className="text-[#D4AF37] font-bold tracking-wide text-sm mb-2">SAT MASTER</div>
            <div className="serif text-lg text-white leading-tight mb-2 group-hover:text-[#D4AF37] transition">Die Spielregeln des Lebens</div>
            <div className="text-xs text-white/60">Persönliche Transformation & Lebensmeisterschaft</div>
          </Link>
          <Link to="/sat-mentor" className="glass rounded-2xl p-6 hover:border-[#D4AF37]/60 border border-white/10 transition group" data-testid="about-link-mentor">
            <div className="text-[#D4AF37] font-bold tracking-wide text-sm mb-2">SAT MENTOR</div>
            <div className="serif text-lg text-white leading-tight mb-2 group-hover:text-[#D4AF37] transition">Ausbildung zum Begleiter</div>
            <div className="text-xs text-white/60">Für Menschen, die selbst begleiten</div>
          </Link>
          <Link to="/herzbewegung" className="glass rounded-2xl p-6 hover:border-[#D4AF37]/60 border border-white/10 transition group" data-testid="about-link-herzbewegung">
            <div className="text-[#D4AF37] font-bold tracking-wide text-sm mb-2">HERZBEWEGUNG</div>
            <div className="serif text-lg text-white leading-tight mb-2 group-hover:text-[#D4AF37] transition">Gemeinschaft & Menschlichkeit</div>
            <div className="text-xs text-white/60">Community, Bewusstsein, gelebte Werte</div>
          </Link>
        </div>
      </div>

      {/* Final CTA */}
      <div className="mt-20 text-center">
        <p className="serif italic text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto leading-snug">
          Ein persönliches Gespräch ersetzt keinen Prospekt.<br />
          Es ersetzt jede Broschüre.
        </p>
        <Link
          to="/erstgespraech"
          className="btn-primary inline-flex items-center gap-2 py-3.5 px-8 text-base"
          data-testid="about-cta-start"
        >
          Kostenloses Erstgespräch buchen →
        </Link>
      </div>
    </div>
  </div>
);
